/**
 * ActionCenterWidget - Widget 2: Action Center
 *
 * Displays trading action data: Signals, Strategy DNA, Patterns, Positions
 * "What should I do?" - Actionable trading decisions
 *
 * Part of dual-widget architecture for scalable indicator display.
 */

import React, { useState, memo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import PsychologyIcon from '@mui/icons-material/Psychology';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlashOnIcon from '@mui/icons-material/FlashOn';

import CollapsibleSection from '../CollapsibleSection';
import SignalsTable from '../tables/SignalsTable';
import PositionSummaryBar from '../PositionSummaryBar';

/**
 * ActionCenterWidget Component
 */
const ActionCenterWidget = memo(({
    strategySignal,
    currentState,
    signal,
    confidence,
    positions = [],
    strategyId = null,
    isCollapsed = false,
    onToggleCollapse,
    collapsible = true
}) => {
    // Track which sections are expanded
    const [expandedSections, setExpandedSections] = useState({
        signals: true,      // Default expanded
        strategyDna: false,
        patterns: false
    });

    const handleSectionToggle = (sectionId, isExpanded) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: isExpanded
        }));
    };

    // Get signal color
    const getSignalColor = (sig) => {
        switch (sig?.toUpperCase()) {
            case 'BUY':
            case 'STRONG_BUY':
                return '#22c55e';
            case 'SELL':
            case 'STRONG_SELL':
                return '#ef4444';
            default:
                return '#fbbf24';
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: '#0f0f0f',
                border: '1px solid #374151',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: isCollapsed ? 'auto' : '180px',
                maxHeight: isCollapsed ? 'auto' : 'calc(50vh - 100px)'
            }}
        >
            {/* Widget Header */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    borderBottom: '2px solid #374151',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 0.5
                }}
            >
                {/* Left: Title + Signal */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FlashOnIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#f59e0b',
                            letterSpacing: '0.5px',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                        }}
                    >
                        Action Center
                    </Typography>
                    {signal && (
                        <>
                            <Box sx={{ width: '1px', height: '12px', backgroundColor: '#4b5563' }} />
                            <Chip
                                label={signal}
                                size="small"
                                sx={{
                                    backgroundColor: getSignalColor(signal),
                                    color: '#fff',
                                    fontSize: '9px',
                                    height: '16px',
                                    fontWeight: 700
                                }}
                            />
                            {confidence && (
                                <Typography
                                    sx={{
                                        fontSize: '9px',
                                        color: '#9ca3af',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {(confidence * 100).toFixed(0)}%
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* Right: Collapse */}
                {collapsible && (
                    <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} arrow>
                        <IconButton
                            onClick={onToggleCollapse}
                            size="small"
                            sx={{ color: '#9ca3af', padding: '2px' }}
                        >
                            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Widget Content */}
            {!isCollapsed && (
                <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {/* SIGNALS Section */}
                        <CollapsibleSection
                            title="Signals"
                            icon={<SignalCellularAltIcon sx={{ fontSize: 14 }} />}
                            badge={signal ? `${signal}` : null}
                            badgeColor={getSignalColor(signal)}
                            defaultExpanded={expandedSections.signals}
                            onToggle={(expanded) => handleSectionToggle('signals', expanded)}
                        >
                            <SignalsTable data={strategySignal} currentState={currentState} />
                        </CollapsibleSection>

                        {/* STRATEGY DNA Section - Phase 0.3 */}
                        <CollapsibleSection
                            title="Strategy DNA"
                            icon={<PsychologyIcon sx={{ fontSize: 14 }} />}
                            phase="0.3"
                            disabled={true}
                            defaultExpanded={false}
                        >
                            <Typography sx={{ fontSize: '11px', color: '#6b7280', padding: '8px' }}>
                                Level performance & fakeout analysis coming in Phase 0.3
                            </Typography>
                        </CollapsibleSection>

                        {/* TOP PATTERNS Section - Phase 0.4 */}
                        <CollapsibleSection
                            title="Top Patterns"
                            icon={<InsightsIcon sx={{ fontSize: 14 }} />}
                            phase="0.4"
                            disabled={true}
                            defaultExpanded={false}
                        >
                            <Typography sx={{ fontSize: '11px', color: '#6b7280', padding: '8px' }}>
                                Pattern discovery & AI insights coming in Phase 0.4
                            </Typography>
                        </CollapsibleSection>
                    </Box>

                    {/* Position Summary Bar - Always visible at bottom */}
                    <PositionSummaryBar
                        positions={positions}
                        strategyId={strategyId}
                        showDetails={true}
                    />
                </Box>
            )}
        </Box>
    );
});

ActionCenterWidget.displayName = 'ActionCenterWidget';

export default ActionCenterWidget;
